import React from 'react';
import {
  ChatBubbleBottomCenterTextIcon,
  CubeIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';
import { getCaraTriggerEmailAddress } from '@oysterjs/core/email';
import { Badge } from '@oysterjs/ui/badge';
import {
  type CaraWorkspacePreset,
  type CaraWorkspaceUseCase,
  TriggerCallRouting,
  TriggerCallType,
  TriggerDefinitionStatus,
  TriggerEventState,
  TriggerEventType,
  TriggerRunState,
  type TriggerDefinitionFieldsFragment,
  type TriggerEventListFieldsFragment,
  useMailboxAccountsQuery
} from '~/api/graphql';
import { familyForPreset, useCaseById } from '~/components/home/use-case-catalog';
import { describeEmailMailboxes } from '~/api/data/triggers/email-summary';

/** Local weekday keys for the schedule UI; mapped to cron DOW (0=Sun … 6=Sat). */
export enum ScheduleWeekday {
  Sun = 'SUN',
  Mon = 'MON',
  Tue = 'TUE',
  Wed = 'WED',
  Thu = 'THU',
  Fri = 'FRI',
  Sat = 'SAT'
}

export const DAYS_LABELS: Record<ScheduleWeekday, string> = {
  [ScheduleWeekday.Sun]: 'Sunday',
  [ScheduleWeekday.Mon]: 'Monday',
  [ScheduleWeekday.Tue]: 'Tuesday',
  [ScheduleWeekday.Wed]: 'Wednesday',
  [ScheduleWeekday.Thu]: 'Thursday',
  [ScheduleWeekday.Fri]: 'Friday',
  [ScheduleWeekday.Sat]: 'Saturday'
};

const WEEKDAY_TO_CRON_DOW: Record<ScheduleWeekday, string> = {
  [ScheduleWeekday.Sun]: '0',
  [ScheduleWeekday.Mon]: '1',
  [ScheduleWeekday.Tue]: '2',
  [ScheduleWeekday.Wed]: '3',
  [ScheduleWeekday.Thu]: '4',
  [ScheduleWeekday.Fri]: '5',
  [ScheduleWeekday.Sat]: '6'
};

const CRON_DOW_TO_WEEKDAY: Record<string, ScheduleWeekday> = {
  '0': ScheduleWeekday.Sun,
  '7': ScheduleWeekday.Sun,
  sun: ScheduleWeekday.Sun,
  '1': ScheduleWeekday.Mon,
  mon: ScheduleWeekday.Mon,
  '2': ScheduleWeekday.Tue,
  tue: ScheduleWeekday.Tue,
  '3': ScheduleWeekday.Wed,
  wed: ScheduleWeekday.Wed,
  '4': ScheduleWeekday.Thu,
  thu: ScheduleWeekday.Thu,
  '5': ScheduleWeekday.Fri,
  fri: ScheduleWeekday.Fri,
  '6': ScheduleWeekday.Sat,
  sat: ScheduleWeekday.Sat
};

/** Claimed Cara email handle only — never invent one from display/legal name. */
export const agencyEmailNamespace = (
  agency?: {
    emailNamespace?: string | null;
  } | null
): string => agency?.emailNamespace?.trim() ?? '';

/** What kind of trigger or event this is, for badges and the detail Type field. */
export const EVENT_TYPE_LABELS: Record<TriggerEventType, string> = {
  [TriggerEventType.EmailReceived]: 'Email',
  [TriggerEventType.ScheduleDue]: 'Schedule',
  [TriggerEventType.CallAnalyzed]: 'Call'
};

/** Title for an event that has no subject of its own. */
export const EVENT_OCCURRENCE_LABELS: Record<TriggerEventType, string> = {
  [TriggerEventType.EmailReceived]: 'Email received',
  [TriggerEventType.ScheduleDue]: 'Scheduled run',
  [TriggerEventType.CallAnalyzed]: 'Call completed'
};

export const CALL_TYPE_LABELS: Record<TriggerCallType, string> = {
  [TriggerCallType.Any]: 'All calls',
  [TriggerCallType.Inbound]: 'Incoming calls only',
  [TriggerCallType.Outbound]: 'Outgoing calls only'
};

export const CALL_ROUTING_LABELS: Record<TriggerCallRouting, string> = {
  [TriggerCallRouting.AnyAccount]: 'All customers',
  [TriggerCallRouting.NewBusiness]: 'New leads',
  [TriggerCallRouting.Service]: 'Existing clients'
};

const pluralize = (count: number, unit: string) => `${count} ${unit}${count === 1 ? '' : 's'}`;

/** The edit form works in minutes, but API-authored triggers can hold any number of seconds. */
export const formatCallDuration = (seconds: number): string =>
  seconds % 60 === 0 ? pluralize(seconds / 60, 'minute') : pluralize(seconds, 'second');

/** Lowercase words joined by single hyphens, matching the server's check on emailSuffix. */
export const EMAIL_SUFFIX_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

export const normalizeEmailSuffix = (suffix: string): string => suffix.trim().toLowerCase();

export const TRIGGERS_PATH = '/app/workflows/triggers';
export const EVENTS_PATH = '/app/workflows/events';
export const triggerHref = (triggerId: string) => `${TRIGGERS_PATH}/${triggerId}`;
export const triggerEditHref = (triggerId: string) => `${TRIGGERS_PATH}/${triggerId}/edit`;
export const eventHref = (eventId: string) => `${EVENTS_PATH}/${eventId}`;

/** Shown where a trigger names a custom preset the viewer can no longer see. */
export const PRESET_UNAVAILABLE = 'Preset unavailable';

export const TriggerStatusBadge = ({ status }: { status: TriggerDefinitionStatus }) =>
  status === TriggerDefinitionStatus.Active ? (
    <Badge color="green">Active</Badge>
  ) : (
    <Badge color="amber">Paused</Badge>
  );

/** Shared by triggers and events, so a type reads the same on every page. */
export const EventTypeBadge = ({ eventType }: { eventType: TriggerEventType }) => (
  <Badge color={eventType === TriggerEventType.ScheduleDue ? 'neutral' : 'sky'}>
    {EVENT_TYPE_LABELS[eventType]}
  </Badge>
);

export const EventStateBadge = ({ state }: { state: TriggerEventState }) => {
  if (state === TriggerEventState.Evaluated) return <Badge color="green">Evaluated</Badge>;
  if (state === TriggerEventState.Failed) return <Badge color="red">Failed</Badge>;
  return <Badge color="sky">Pending</Badge>;
};

export const RunStateBadge = ({ state }: { state: TriggerRunState }) => {
  if (state === TriggerRunState.Launched) return <Badge color="green">Launched</Badge>;
  if (state === TriggerRunState.Failed) return <Badge color="red">Failed</Badge>;
  return <Badge color="amber">Queued</Badge>;
};

export const compareText = (a: string, b: string) =>
  a.localeCompare(b, undefined, { sensitivity: 'base' });

/** Sorts ascending by `compare`, reversed for a descending column. */
export const sortRows = <T,>(
  rows: T[],
  compare: (a: T, b: T) => number,
  direction: 'asc' | 'desc'
): T[] => {
  const sorted = [...rows].sort(compare);
  return direction === 'desc' ? sorted.reverse() : sorted;
};

/** Whether a trigger names connected mailboxes that useMailboxLabel should look up. */
export const namesMailboxAccounts = (trigger: TriggerDefinitionFieldsFragment): boolean =>
  !!trigger.eventMatcher.mailboxConfig ||
  (trigger.eventMatcher.emailConfig?.scope.accounts.length ?? 0) > 0;

/**
 * Labels a connected mailbox by its address. Only mailboxes the caller may send
 * from are readable, so a colleague's mailbox falls back to a generic label.
 */
export const useMailboxLabel = (skip: boolean) => {
  const { data } = useMailboxAccountsQuery({ skip });
  const addresses = React.useMemo(
    () => new Map((data?.mailboxAccounts ?? []).map((a) => [a.id, a.identity.email])),
    [data]
  );
  return React.useCallback(
    (integrationId: string) => addresses.get(integrationId) ?? 'Connected mailbox',
    [addresses]
  );
};

/** Build a 5-field cron from local time (HH:MM) and weekdays. */
export const scheduleToCron = (time: string, days: ScheduleWeekday[]): string => {
  const [hourRaw, minuteRaw] = time.split(':');
  const hour = Number(hourRaw);
  const minute = Number(minuteRaw);
  if (
    !Number.isInteger(hour) ||
    !Number.isInteger(minute) ||
    hour < 0 ||
    hour > 23 ||
    minute < 0 ||
    minute > 59 ||
    days.length === 0
  ) {
    throw new Error('Invalid schedule time or days');
  }
  const dow = [...days]
    .map((day) => WEEKDAY_TO_CRON_DOW[day])
    .sort((a, b) => Number(a) - Number(b))
    .join(',');
  return `${minute} ${hour} * * ${dow}`;
};

/** Parse a weekday+time cron the UI understands; returns null for unsupported expressions. */
export const cronToSchedule = (cron: string): { time: string; days: ScheduleWeekday[] } | null => {
  const parts = cron.trim().split(/\s+/);
  if (parts.length !== 5) return null;
  const [minuteField, hourField, dom, month, dowField] = parts;
  if (dom !== '*' || month !== '*') return null;
  const minute = Number(minuteField);
  const hour = Number(hourField);
  if (
    !Number.isInteger(minute) ||
    !Number.isInteger(hour) ||
    minute < 0 ||
    minute > 59 ||
    hour < 0 ||
    hour > 23
  ) {
    return null;
  }
  const days = dowField
    .toLowerCase()
    .split(',')
    .flatMap((token) => {
      const range = token.split('-');
      if (range.length === 2) {
        const start = Number(range[0]);
        const end = Number(range[1]);
        if (!Number.isInteger(start) || !Number.isInteger(end) || start > end) return [];
        return Array.from({ length: end - start + 1 }, (_, i) => String(start + i));
      }
      return [token];
    })
    .map((token) => CRON_DOW_TO_WEEKDAY[token])
    .filter((day): day is ScheduleWeekday => day != null);
  if (days.length === 0) return null;
  const uniqueDays = [...new Set(days)];
  return {
    time: `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`,
    days: uniqueDays
  };
};

/** Day names in week order, whatever order they were picked in. */
export const formatScheduleDays = (days: ScheduleWeekday[]): string =>
  [...days]
    .sort((a, b) => Number(WEEKDAY_TO_CRON_DOW[a]) - Number(WEEKDAY_TO_CRON_DOW[b]))
    .map((day) => DAYS_LABELS[day])
    .join(', ');

export const formatScheduleDescription = (cron: string, timezone: string): string => {
  const parsed = cronToSchedule(cron);
  if (!parsed) return `${cron} · ${timezone}`;
  return `${parsed.time} · ${formatScheduleDays(parsed.days)} · ${timezone}`;
};

export const matchedRunCount = (event: { runs?: ReadonlyArray<unknown> | null }): number =>
  event.runs?.length ?? 0;

/** "2 triggers matched", or an empty string when nothing matched. */
export const formatMatchCount = (count: number): string =>
  count > 0 ? `${pluralize(count, 'trigger')} matched` : '';

export const triggerSourceDescription = (
  trigger: TriggerDefinitionFieldsFragment,
  emailNamespace: string,
  mailboxLabel: (integrationId: string) => string
): string => {
  const matcher = trigger.eventMatcher;
  if (matcher.eventType === TriggerEventType.EmailReceived) {
    if (matcher.emailConfig) {
      return describeEmailMailboxes(matcher.emailConfig, { mailbox: mailboxLabel });
    }
    if (matcher.mailboxConfig?.integrationId) {
      return mailboxLabel(matcher.mailboxConfig.integrationId);
    }
    if (matcher.caraAddressConfig?.emailSuffix && emailNamespace) {
      return getCaraTriggerEmailAddress(emailNamespace, matcher.caraAddressConfig.emailSuffix);
    }
    return matcher.caraAddressConfig?.emailSuffix
      ? getCaraTriggerEmailAddress('x', matcher.caraAddressConfig.emailSuffix).replace('-x-', '-…-')
      : 'Cara email address';
  }
  if (matcher.eventType === TriggerEventType.ScheduleDue) {
    const schedule = matcher.scheduleConfig;
    return schedule ? formatScheduleDescription(schedule.cron, schedule.timezone) : 'Schedule';
  }
  const call = matcher.callAnalyzedConfig;
  if (!call) return 'Completed call';
  const parts = [CALL_TYPE_LABELS[call.callType], CALL_ROUTING_LABELS[call.routing]];
  if (call.minimumDurationSeconds > 0) {
    parts.push(`${formatCallDuration(call.minimumDurationSeconds)} minimum`);
  }
  return parts.join(' · ');
};

/**
 * Title of a built-in target or form task, or undefined when it has no built-in
 * preset. Falls back to the preset family for a use case the catalog dropped.
 */
export const builtInTaskTitle = (task: {
  preset?: CaraWorkspacePreset | null;
  useCase?: CaraWorkspaceUseCase | null;
}): string | undefined => {
  if (!task.preset) return undefined;
  const useCase = task.useCase ? useCaseById(task.useCase) : undefined;
  return useCase?.title ?? familyForPreset(task.preset)?.title ?? 'Built-in preset';
};

/** What a trigger launches, in the shape the list renders it. */
export type TriggerTaskSummary = {
  /** Absent for a prompt-only trigger. `title` is undefined when a custom preset was deleted or unshared. */
  preset?: { kind: 'builtIn' | 'custom'; title?: string };
  /**
   * Set only for a prompt-only trigger; a preset's extra instructions stay off the list.
   * Collapsed to one line so a preview truncates instead of wrapping.
   */
  prompt?: string;
};

export const triggerTaskSummary = (
  target: TriggerDefinitionFieldsFragment['target'],
  customPresetTitles: Map<string, string>
): TriggerTaskSummary => {
  const builtIn = builtInTaskTitle(target);
  const preset = builtIn
    ? { kind: 'builtIn' as const, title: builtIn }
    : target.presetId
      ? { kind: 'custom' as const, title: customPresetTitles.get(target.presetId) }
      : undefined;
  if (preset) return { preset };
  return { prompt: target.prompt?.replace(/\s+/g, ' ').trim() || undefined };
};

export const triggerTaskSearchText = (task: TriggerTaskSummary): string =>
  [task.preset?.title, task.prompt].filter(Boolean).join(' ');

export const TASK_ICONS = {
  builtIn: { icon: SparklesIcon, className: 'text-sky-600 dark:text-sky-400' },
  custom: { icon: CubeIcon, className: 'text-purple-600 dark:text-purple-400' },
  prompt: {
    icon: ChatBubbleBottomCenterTextIcon,
    className: 'text-neutral-600 dark:text-neutral-400'
  }
};

/** Names the preset, or previews the prompt of a prompt-only trigger. The icon says which kind of task it is. */
export const TriggerTaskCell = ({ task }: { task: TriggerTaskSummary }) => {
  const { preset, prompt } = task;
  if (!preset && !prompt) {
    return <span className="text-sm text-neutral-400 dark:text-neutral-500">No task</span>;
  }
  const { icon: Icon, className: iconClassName } = TASK_ICONS[preset?.kind ?? 'prompt'];
  const label = preset ? (preset.title ?? PRESET_UNAVAILABLE) : `“${prompt}”`;

  return (
    <span className="flex min-w-0 items-start gap-1.5" title={label}>
      <Icon aria-hidden="true" className={`mt-0.5 size-4 shrink-0 ${iconClassName}`} />
      <span
        className={
          preset && !preset.title
            ? 'truncate text-sm italic text-neutral-400 dark:text-neutral-500'
            : 'truncate text-sm text-neutral-700 dark:text-neutral-300'
        }
      >
        {label}
      </span>
    </span>
  );
};

export const eventSummary = (
  event: Pick<TriggerEventListFieldsFragment, 'payload' | 'eventType'>
): string => {
  const subject = typeof event.payload?.subject === 'string' ? event.payload.subject : '';
  return subject || EVENT_OCCURRENCE_LABELS[event.eventType];
};

export const formatEventTimestamp = (value: Date | string) =>
  new Intl.DateTimeFormat('en-US', { dateStyle: 'medium', timeStyle: 'short' }).format(
    new Date(value)
  );
